import { TAvsApiData } from "@components/AVS/types";
import axios from "axios";
import { BigNumber } from "ethers";

interface subscribeToEmailProps {
  email: string;
  list_uuids: string[];
}

const COMMON_API_ENDPOINT = process.env.NEXT_PUBLIC_COMMON_API_URL;
const UNIVERSE_API_ENDPOINT = process.env.NEXT_PUBLIC_UNIVERSE_API_URL;
const COMMON_KERNELDAO_API_ENDPOINT =
  process.env.NEXT_PUBLIC_COMMON_KERNELDAO_API_URL;

export const geoAPI = () => {
  return axios.get(`${UNIVERSE_API_ENDPOINT}/rseth/geoData`);
};

export const subscribeEmail = async (data: subscribeToEmailProps) => {
  return axios.post(
    "https://script.google.com/macros/s/AKfycbxBOMrpVjekduyQdbzxbwbRV9eBQHeAl3k9Er1iy5ztWggoKKKRDalD-w_CVMfsdJP-EA/exec",
    data
  );
};

export const getDashboardata = (address: string) => {
  return axios.get(`${COMMON_API_ENDPOINT}/km-el-points/user/${address}`);
};

export const getSeasonWisePoints = (address: string) => {
  return axios.get(
    `${COMMON_API_ENDPOINT}/el-merkle/seasonWisePoints/${address}`
  );
};

export const getRewardsPointdata = (address: string) => {
  return axios.get(`${COMMON_API_ENDPOINT}/gain/user/${address}`);
};

export const getExchangeRate = (selectedERC20: string) => {
  return axios.get(
    `${UNIVERSE_API_ENDPOINT}/rseth/exchangeRate/?lrtToken=${selectedERC20}`
  );
};

export const getVaultChartData = (asset: string) => {
  return axios.get(`${COMMON_KERNELDAO_API_ENDPOINT}/apr?asset=${asset}`);
};

export const getAssetsPriceUsd = (assets: string[]) => {
  return axios.get(
    `https://universe.kelpdao.xyz/coingecko/simple/price?ids=${assets.join(
      ","
    )}&vs_currencies=usd`
  );
};

export interface TVLResponse {
  value: number;
  usdTvl: number;
}

export interface GainTVLResponse {
  tvl: number;
  agEthTvl: number;
  hgEthTvl: number;
  sbUsdTvl: number;
}

export interface TotalTVLResponse {
  tvl: number;
}

export interface VaultPerformanceResponse {
  vaultPerformance: Array<{
    date: string;
    agEthValue: number;
    hgEthValue: number;
  }>;
}

export type TAvsResponse = {
  avs_items: Record<string, TAvsApiData>;
  nodes_data: Record<string, TAvsApiData>;
};

export type TGainRewardAsset = {
  id: number;
  name: number;
  net_usd_value: number;
};

export type TGainRewards = {
  gainRewards: TGainRewardAsset[];
};

export const getTVL = () => {
  return axios.get<TVLResponse>(`${UNIVERSE_API_ENDPOINT}/rseth/tvl/?lrtToken`);
};

export const getTotalTVL = () => {
  return axios.get<TotalTVLResponse>(`${UNIVERSE_API_ENDPOINT}/rseth/totalTvl`);
};

export const getVaultPerformance = () => {
  return axios.get<VaultPerformanceResponse>(
    `${UNIVERSE_API_ENDPOINT}/rseth/vaultPerformance`
  );
};

export const getGainTVL = () => {
  return axios.get<GainTVLResponse>(`${UNIVERSE_API_ENDPOINT}/rseth/gainTvl`);
};

export const getAVSData = () => {
  return axios.get<TAvsResponse>(`${UNIVERSE_API_ENDPOINT}/rseth/avsData`);
};
export const getGainRewards = () => {
  return axios.get<TGainRewards>(
    `${UNIVERSE_API_ENDPOINT}/rseth/getGainRewards`
  );
};

export const getPoolLimit = (selectedERC20: string) => {
  return axios.get(
    `${UNIVERSE_API_ENDPOINT}/rseth/maxDepositLimit/?lrtToken=${selectedERC20}`
  );
};

export const getPoolStakedValue = (selectedERC20: string) => {
  return axios.get(
    `${UNIVERSE_API_ENDPOINT}/rseth/totalStaked/?lrtToken=${selectedERC20}`
  );
};

export interface KMELPointsResponse {
  time: string;
  value: {
    elPoints: string;
  };
}

export const getKelpStats = (address: `0x${string}`) => {
  return axios.get<KMELPointsResponse>(
    `${COMMON_API_ENDPOINT}/km-el-points/user/${address}`
  );
};

export interface TotalKMELPointsResponse {
  time: string;
  value: {
    latestProcessedSfrxEthDepositsBlockNumber: number;
    createdAt: number;
    latestDayBlockNumber: number;
    deposits: string;
    points: {
      eth: string;
      eigenLayerPoints: string;
      kelpMiles: string;
    };
    sk: string;
    latestProcessedStEthSharesBlockNumber: number;
    latestProccessedPointsBlockNumber: number;
    latestProcessedEthxDepositsBlockNumber: number;
    sfrxEthDeposits: string;
    pk: string;
    shares: string;
  };
}

export const getTotalKMELPoints = () => {
  return axios.get<TotalKMELPointsResponse>(
    `${COMMON_API_ENDPOINT}/km-el-points-debug/total`
  );
};

export interface ElMerkleProofResponse {
  data: {
    proof: `0x${string}`[];
    el: string;
    createdAt: string;
    sk: `0x${string}`;
    pk: string;
  } | null;
  time: string;
}
export const getElMerkleProof = (address: `0x${string}`, index: BigNumber) => {
  return axios.get<ElMerkleProofResponse>(
    `${COMMON_API_ENDPOINT}/el-merkle/proofs/index/${index.toString()}/${address}`
  );
};
export interface ElMerkleRootResponse {
  data: {
    root: string;
    km: string;
    el: string;
    createdAt: string;
    sk: string;
    pk: string;
    totalUsers: number;
    totalElPoints: string;
  };
  time: string;
}

export interface ApyResponse {
  kelpAPY: number;
  eigenAPY: number;
  totalAPY: number;
  roundedTotalAPY: number;
  is7DaysKelpAPY: boolean;
}

export type TGainApyResponse = {
  agETH: string;
  hgETH: string;
  tacRsETH?: string;
  tacBTC?: string;
  rsETH?: string;
  xgUSD?: string;
};

export const getElMerkleRoot = () => {
  return axios.get<ElMerkleRootResponse>(
    `${COMMON_API_ENDPOINT}/el-merkle/root`
  );
};

export const getRestakeApy = () => {
  return axios.get<ApyResponse>(`${UNIVERSE_API_ENDPOINT}/rseth/totalApy`);
};
export const getGainApy = () => {
  return axios.get<TGainApyResponse>(`${UNIVERSE_API_ENDPOINT}/rseth/gainApy`);
};

export interface ElMerkleProofsResponse {
  data: {
    proofs: `0x${string}`[];
    el: string;
    createdAt: string;
    sk: `0x${string}`;
    pk: string;
    km: string;
  } | null;
  programmatic: {
    proofs: `0x${string}`[];
    el: string;
    root: `0x${string}`;
  };
  time: string;
}

export const getElMerkleProofList = (address: `0x${string}`) => {
  return axios.get<ElMerkleProofsResponse>(
    // TODO: confirm with rahul how 104 ?
    `https://common.kelpdao.xyz/el-merkle/proofs/${address.toLowerCase()}`
  );
};

export interface KernelMerkleProofsResponse {
  data: {
    balance: string;
    updatedAt: string;
    proof: `0x${string}`[];
    createdAt: string;
    totalBalance: string;
    isTop100User?: boolean;
    user: string;
    sk: `0x${string}`;
    pk: string;
  } | null;
  time: string;
}

export const getKernelS3V1MerkleProofListEth = (address: `0x${string}`) => {
  return axios.get<KernelMerkleProofsResponse>(
    `https://common.kerneldao.com/merkle/proofs/kernel_eth_season_3?address=${address.toLowerCase()}`
  );
};

export const getKernelS3V1MerkleProofListBsc = (address: `0x${string}`) => {
  return axios.get<KernelMerkleProofsResponse>(
    `https://common.kerneldao.com/merkle/proofs/kernel_bnb_season_3?address=${address.toLowerCase()}`
  );
};

export interface ScrollMerkleProofsResponse {
  data: {
    proof: `0x${string}`[];
    marks: string;
    createdAt: string;
    sk: `0x${string}`;
    pk: string;
    km: string;
  } | null;
  time: string;
}

export const getScrollMerkleProofList = (address: `0x${string}`) => {
  return axios.get<ScrollMerkleProofsResponse>(
    `https://common.kelpdao.xyz/gain/scrollProof/${address.toLowerCase()}`
  );
};

export const getVaultcraftData = () => {
  const API_URL = "https://app.vaultcraft.io/api/vaults?chainId=1";
  return axios.get(API_URL);
};

export interface SanctionStatusResponse {
  isSanctioned: boolean;
  createdAt: number;
  data: {
    success: boolean;
    message: string;
    timestamp: string;
    client: string;
    data: {
      address: string;
      entity: {
        type: string;
        name: string | null;
      };
      risk_assessment: {
        risk_score: number;
        risk_level: string;
      };
      recommendation: string;
      classification: string;
      risk_tags: string;
      priority: string;
      labels: string[];
      chains_with_activity: string[];
    };
  };
}

export const fetchSanctionStatus = async (
  walletAddress: string
): Promise<SanctionStatusResponse> => {
  const { data } = await axios.get<SanctionStatusResponse>(
    `${UNIVERSE_API_ENDPOINT}/common/getWalletSanctionData?walletAddress=${walletAddress}`
  );
  return data;
};

export interface KernelMerkleProofResponse {
  data: {
    balance: string;
    kernelPoints: string;
  } | null;
  time: string;
}

export const checkTokenEligibilityETH = (address: `0x${string}`) => {
  return axios.get<KernelMerkleProofResponse>(
    `https://common.kerneldao.com/merkle/proofs/kernel_eth?address=${address.toLowerCase()}`
  );
};

export const checkTokenEligibilityBNB = (address: `0x${string}`) => {
  return axios.get<KernelMerkleProofResponse>(
    `https://common.kerneldao.com/merkle/proofs/kernel_bnb?address=${address.toLowerCase()}`
  );
};
